import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  User, Lock, CalendarIcon, Users, Plus, RefreshCw, 
  CheckCircle, XCircle, Share2, Trash2, Loader2, Mail, Send, FileText, Landmark
} from 'lucide-react';
import { AIWordingButton } from './AIWordingButton';
import { ChatGPTConnectorCard } from './ChatGPTConnectorCard';
import { StorageCleanupCard } from './StorageCleanupCard';

const API = window.location.origin;
const TEAM_COLORS = [
  "#5D7C3F", "#3B82F6", "#EF4444", "#F59E0B", "#8B5CF6",
  "#EC4899", "#06B6D4", "#F97316"
];

// Inline card for managing accountant email + one-click Tax Year email send
const AccountantSettingsCard = ({ authHeaders }) => {
  const [settings, setSettings] = React.useState({ email: '', name: '' });
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [overrideEmail, setOverrideEmail] = React.useState('');

  const fetchSettings = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/settings/accountant`, authHeaders);
      setSettings({ email: res.data.email || '', name: res.data.name || '' });
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [authHeaders]);

  React.useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const [justSaved, setJustSaved] = React.useState(false);
  const justSavedTimerRef = React.useRef(null);

  React.useEffect(() => () => {
    if (justSavedTimerRef.current) clearTimeout(justSavedTimerRef.current);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/accountant`, settings, authHeaders);
      toast.success(`Accountant email saved: ${settings.email || 'cleared'}`, { duration: 4000 });
      setJustSaved(true);
      if (justSavedTimerRef.current) clearTimeout(justSavedTimerRef.current);
      justSavedTimerRef.current = setTimeout(() => setJustSaved(false), 3000);
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSend = async () => {
    const target = overrideEmail || settings.email;
    if (!target) { toast.error('Enter or save an accountant email first'); return; }
    if (!window.confirm(`Email the current tax-year PDF + CSV to ${target}?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/admin/tax/send-to-accountant`,
        overrideEmail ? { email: overrideEmail } : {},
        authHeaders);
      toast.success(res.data?.message || 'Sent!');
      setOverrideEmail('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <Card data-testid="accountant-settings-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Accountant & Tax Year Reports
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Accountant email</Label>
            <Input
              type="email"
              value={settings.email}
              onChange={(e) => setSettings(p => ({ ...p, email: e.target.value }))}
              placeholder="accounts@youraccountant.co.uk"
              data-testid="accountant-email-input"
            />
          </div>
          <div>
            <Label>Accountant name (optional)</Label>
            <Input
              value={settings.name}
              onChange={(e) => setSettings(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Sarah at Foxtrot Accountants"
              data-testid="accountant-name-input"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button onClick={handleSave} disabled={saving || loading} data-testid="accountant-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
            Save email
          </Button>
          {justSaved && (
            <span
              className="inline-flex items-center gap-1 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 animate-pulse"
              data-testid="accountant-saved-indicator"
            >
              <CheckCircle className="h-4 w-4" /> Saved & active
            </span>
          )}
        </div>

        <div className="border-t pt-3 space-y-2">
          <Label className="text-xs text-muted-foreground">One-click send: attaches this tax year's PDF + CSV.</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              placeholder={settings.email ? `Override (blank = send to ${settings.email})` : 'Enter a different email'}
              data-testid="accountant-override-email"
            />
            <Button
              onClick={handleSend}
              disabled={sending || (!settings.email && !overrideEmail)}
              className="bg-primary"
              data-testid="send-to-accountant-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send to accountant
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Business & payment details shown on invoice + payment-request PDFs.
const BusinessDetailsCard = ({ authHeaders }) => {
  const FIELDS = useMemo(() => ([
    { key: 'business_name', label: 'Business Name', placeholder: 'Sustainable Handyman Services' },
    { key: 'business_phone', label: 'Phone', placeholder: '07359 139941' },
    { key: 'business_email', label: 'Email', placeholder: 'you@example.co.uk' },
    { key: 'business_address', label: 'Address', placeholder: 'Unit 7, Blaenant Industrial Estate, Blaenavon Rd, Brynmawr, NP23 4BX' },
  ]), []);
  const BANK_FIELDS = useMemo(() => ([
    { key: 'bank_account_name', label: 'Account Name', placeholder: 'Sandy Restall' },
    { key: 'bank_sort_code', label: 'Sort Code', placeholder: '04-00-03' },
    { key: 'bank_account_number', label: 'Account Number', placeholder: '93331409' },
  ]), []);

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/settings`, authHeaders);
      setData(res.data || {});
    } catch (e) {
      toast.error('Could not load business details');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/admin/settings`, data, authHeaders);
      setData(res.data || {});
      toast.success('Business details saved — website, emails and PDFs will use these.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="business-details-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Landmark className="h-5 w-5" />
          Business & Payment Details
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Used everywhere your details appear — website footer, About page, enquiry emails, invoices, payment requests and supplier order PDFs. Edit once, everything updates.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Business contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  value={data[f.key] ?? ''}
                  onChange={(e) => setData(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="mt-1"
                  disabled={loading}
                  data-testid={`settings-${f.key.replace(/_/g, '-')}`}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Bank details (for payment)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BANK_FIELDS.map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  value={data[f.key] ?? ''}
                  onChange={(e) => setData(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="mt-1"
                  disabled={loading}
                  data-testid={`settings-${f.key.replace(/_/g, '-')}`}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Invoice wording</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Default wording printed on invoices. You can still override the terms per-invoice from its edit screen.
          </p>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>Payment terms</Label>
                <AIWordingButton
                  value={data.invoice_payment_terms ?? ''}
                  onApply={(t) => setData(p => ({ ...p, invoice_payment_terms: t }))}
                  context="the default payment terms printed on invoices"
                  testId="ai-invoice-payment-terms"
                />
              </div>
              <Textarea
                value={data.invoice_payment_terms ?? ''}
                onChange={(e) => setData(p => ({ ...p, invoice_payment_terms: e.target.value }))}
                placeholder="e.g. Payment due within 7 days via bank transfer."
                className="mt-1"
                rows={2}
                disabled={loading}
                data-testid="settings-invoice-payment-terms"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Late-payment terms</Label>
                <AIWordingButton
                  value={data.invoice_late_payment_terms ?? ''}
                  onApply={(t) => setData(p => ({ ...p, invoice_late_payment_terms: t }))}
                  context="the late-payment terms printed on invoices"
                  testId="ai-invoice-late-payment-terms"
                />
              </div>
              <Textarea
                value={data.invoice_late_payment_terms ?? ''}
                onChange={(e) => setData(p => ({ ...p, invoice_late_payment_terms: e.target.value }))}
                placeholder="e.g. Late payments may incur interest at 8% above the Bank of England base rate."
                className="mt-1"
                rows={2}
                disabled={loading}
                data-testid="settings-invoice-late-payment-terms"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Footer note</Label>
                  <AIWordingButton
                    value={data.invoice_footer_note ?? ''}
                    onApply={(t) => setData(p => ({ ...p, invoice_footer_note: t }))}
                    context="the short footer note printed at the bottom of invoices"
                    testId="ai-invoice-footer-note"
                  />
                </div>
                <Input
                  value={data.invoice_footer_note ?? ''}
                  onChange={(e) => setData(p => ({ ...p, invoice_footer_note: e.target.value }))}
                  placeholder="Thank you for your business."
                  className="mt-1"
                  disabled={loading}
                  data-testid="settings-invoice-footer-note"
                />
              </div>
              <div>
                <Label>VAT line</Label>
                <Input
                  value={data.vat_note ?? ''}
                  onChange={(e) => setData(p => ({ ...p, vat_note: e.target.value }))}
                  placeholder="Not VAT Registered"
                  className="mt-1"
                  disabled={loading}
                  data-testid="settings-vat-note"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold mb-2 text-muted-foreground">Enquiry response</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Sent automatically when someone submits an enquiry. Use {'{name}'}, {'{service}'}, {'{reference}'}, {'{phone}'} and {'{business}'} — they're replaced with the real details.
          </p>
          <div className="space-y-4">
            <div>
              <Label>Email subject</Label>
              <Input
                value={data.enquiry_reply_subject ?? ''}
                onChange={(e) => setData(p => ({ ...p, enquiry_reply_subject: e.target.value }))}
                placeholder="Enquiry Received - {service} Services"
                className="mt-1"
                disabled={loading}
                data-testid="settings-enquiry-reply-subject"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Email message</Label>
                <AIWordingButton
                  value={data.enquiry_reply_message ?? ''}
                  onApply={(t) => setData(p => ({ ...p, enquiry_reply_message: t }))}
                  context="the full body of the automatic enquiry confirmation email sent to customers — greeting, message and sign-off. Placeholders like {name}, {service}, {phone}, {business} must be kept exactly as-is"
                  testId="ai-enquiry-reply-message"
                />
              </div>
              <Textarea
                value={data.enquiry_reply_message ?? ''}
                onChange={(e) => setData(p => ({ ...p, enquiry_reply_message: e.target.value }))}
                placeholder={"Thank you for your enquiry, {name}!\nWe've received your request for {service} services.\nOur team will review your enquiry and get back to you within 24-48 hours.\nIf you have any urgent questions, please call us at {phone}.\nBest regards,\nSandy\n{business}"}
                className="mt-1"
                rows={8}
                disabled={loading}
                data-testid="settings-enquiry-reply-message"
              />
              <p className="text-xs text-muted-foreground mt-1">This is the entire email body — greeting, message and sign-off. Each new line becomes a paragraph; the first line is styled as the heading. The reference ID box and your address footer are added automatically.</p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>On-screen confirmation (website)</Label>
                <AIWordingButton
                  value={data.enquiry_success_message ?? ''}
                  onApply={(t) => setData(p => ({ ...p, enquiry_success_message: t }))}
                  context="the on-screen thank-you message shown on the website right after a customer submits an enquiry"
                  testId="ai-enquiry-success-message"
                />
              </div>
              <Textarea
                value={data.enquiry_success_message ?? ''}
                onChange={(e) => setData(p => ({ ...p, enquiry_success_message: e.target.value }))}
                placeholder="Your enquiry has been submitted successfully. We'll get back to you within 24-48 hours."
                className="mt-1"
                rows={2}
                disabled={loading}
                data-testid="settings-enquiry-success-message"
              />
            </div>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || loading} data-testid="business-details-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          Save business details
        </Button>
      </CardContent>
    </Card>
  );
};

export const SettingsTab = ({ token, onProfileUpdate }) => {
  const [adminProfile, setAdminProfile] = useState(null);
  const [allAdmins, setAllAdmins] = useState([]);
  const [profileData, setProfileData] = useState({ name: '', email: '', phone: '', address: '', color: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new_password: '', confirm: '' });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isAddAdminOpen, setIsAddAdminOpen] = useState(false);
  const [isShareCalendarOpen, setIsShareCalendarOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Fetch admin profile
  const fetchAdminProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/profile`, authHeaders);
      setAdminProfile(response.data);
      setProfileData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        color: response.data.color || TEAM_COLORS[0]
      });
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  }, [authHeaders]);

  // Fetch all admins
  const fetchAllAdmins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/admin/all-admins`, authHeaders);
      setAllAdmins(response.data || []);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAdminProfile();
    fetchAllAdmins();
  }, [fetchAdminProfile, fetchAllAdmins]);

  // Update profile
  const handleUpdateProfile = async () => {
    setIsSavingProfile(true);
    try {
      await axios.put(`${API}/api/admin/profile`, profileData, authHeaders);
      toast.success("Profile updated!");
      fetchAdminProfile();
      if (onProfileUpdate) onProfileUpdate();
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setIsSavingPassword(true);
    try {
      await axios.post(`${API}/api/admin/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new_password
      }, authHeaders);
      toast.success("Password changed!");
      setPasswordData({ current: '', new_password: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setIsSavingPassword(false);
    }
  };

  // Add team member
  const handleAddAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password) {
      toast.error("Email and password are required");
      return;
    }
    try {
      await axios.post(`${API}/api/admin/register`, newAdmin, authHeaders);
      toast.success("Team member added!");
      setIsAddAdminOpen(false);
      setNewAdmin({ email: '', password: '', name: '' });
      fetchAllAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add team member");
    }
  };

  // Delete team member
  const handleDeleteAdmin = async (adminId) => {
    if (!confirm("Remove this team member?")) return;
    try {
      await axios.delete(`${API}/api/admin/${adminId}`, authHeaders);
      toast.success("Team member removed");
      fetchAllAdmins();
    } catch (error) {
      toast.error("Failed to remove team member");
    }
  };

  // Sync to Google Calendar - Improved
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSyncAllToGoogle = async () => {
    setIsSyncing(true);
    try {
      const response = await axios.post(`${API}/api/admin/google/sync-all`, {}, authHeaders);
      const { synced_count, updated_count, removed_count, errors } = response.data;
      
      let message = `Sync complete: ${synced_count} new`;
      if (updated_count > 0) message += `, ${updated_count} updated`;
      if (removed_count > 0) message += `, ${removed_count} removed`;
      
      toast.success(message);
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} events had issues - check console for details`);
        if (process.env.NODE_ENV === "development") console.warn("Sync errors:", errors);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync");
    } finally {
      setIsSyncing(false);
    }
  };

  // Test email notification
  const [isTesting, setIsTesting] = useState(false);
  const handleTestNotification = async () => {
    setIsTesting(true);
    try {
      const response = await axios.post(`${API}/api/admin/notifications/test`, {}, authHeaders);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Display Name</Label>
              <Input 
                value={profileData.name}
                onChange={(e) => setProfileData(p => ({ ...p, name: e.target.value }))}
                placeholder="Your name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email Address</Label>
              <Input 
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData(p => ({ ...p, email: e.target.value }))}
                placeholder="your@email.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input 
                value={profileData.phone}
                onChange={(e) => setProfileData(p => ({ ...p, phone: e.target.value }))}
                placeholder="07xxx xxxxxx"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input 
                value={profileData.address}
                onChange={(e) => setProfileData(p => ({ ...p, address: e.target.value }))}
                placeholder="Your address"
                className="mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Calendar Colour</Label>
              <div className="flex items-center gap-3 mt-2">
                <div 
                  className="w-10 h-10 rounded-lg border-2 border-gray-200"
                  style={{ backgroundColor: profileData.color || TEAM_COLORS[0] }}
                ></div>
                <div className="flex gap-2 flex-wrap">
                  {TEAM_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => setProfileData(p => ({ ...p, color }))}
                      className={`w-8 h-8 rounded-lg transition-all ${profileData.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <Input
                  type="color"
                  value={profileData.color || TEAM_COLORS[0]}
                  onChange={(e) => setProfileData(p => ({ ...p, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                  title="Pick custom colour"
                />
              </div>
            </div>
          </div>
          <Button onClick={handleUpdateProfile} disabled={isSavingProfile}>
            {isSavingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Profile
          </Button>
        </CardContent>
      </Card>

      {/* Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Current Password</Label>
              <Input 
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData(p => ({ ...p, current: e.target.value }))}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            <div>
              <Label>New Password</Label>
              <Input 
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(p => ({ ...p, new_password: e.target.value }))}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input 
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData(p => ({ ...p, confirm: e.target.value }))}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
          </div>
          <Button onClick={handleChangePassword} disabled={isSavingPassword}>
            {isSavingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Business & Payment Details Card */}
      <BusinessDetailsCard authHeaders={authHeaders} />

      {/* ChatGPT connector Card */}
      <ChatGPTConnectorCard authHeaders={authHeaders} />

      {/* Storage cleanup Card */}
      <StorageCleanupCard authHeaders={authHeaders} />

      {/* Accountant / Tax Year email Card */}
      <AccountantSettingsCard authHeaders={authHeaders} />

      {/* Google Calendar Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Google Calendar Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <img src="https://www.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png" alt="Google Calendar" className="h-8 w-8" />
              <div>
                <p className="font-medium">Google Calendar</p>
                <p className="text-sm text-muted-foreground">
                  {adminProfile?.google_email ? `Connected: ${adminProfile.google_email}` : 'Not connected'}
                </p>
              </div>
            </div>
            {adminProfile?.google_email ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleSyncAllToGoogle} disabled={isSyncing}>
                  {isSyncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  {isSyncing ? 'Syncing...' : 'Sync Now'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/admin/google/disconnect`, {}, authHeaders);
                      toast.success("Google Calendar disconnected");
                      fetchAdminProfile();
                    } catch (error) {
                      toast.error("Failed to disconnect");
                    }
                  }}
                  className="text-red-600"
                >
                  <XCircle className="h-4 w-4 mr-1" />Disconnect
                </Button>
              </div>
            ) : (
              <Button 
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/api/auth/google/login?admin_id=${adminProfile?.id}`);
                    window.location.href = response.data.authorization_url;
                  } catch (error) {
                    toast.error("Failed to start Google auth");
                  }
                }}
              >
                Connect Google Calendar
              </Button>
            )}
          </div>
          {adminProfile?.google_email && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">📅 Calendar Sync Features</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✅ Jobs sync as single block events (not hourly chunks)</li>
                <li>✅ Full job descriptions included in event details</li>
                <li>✅ Customer name, address, and phone in event</li>
                <li>✅ Cancelled jobs removed from Google Calendar</li>
                <li>✅ No duplicate events - smart update detection</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Notifications Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-muted-foreground">
                  {adminProfile?.google_email ? `Via Gmail: ${adminProfile.google_email}` : 'Via SMTP: sandy@sustainablehandymanservices.uk'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800">Active</Badge>
              <Button variant="outline" size="sm" onClick={handleTestNotification} disabled={isTesting}>
                {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Test'}
              </Button>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">📧 Automatic Notifications</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>✅ New enquiry received → Team notified</li>
              <li>✅ Job scheduled/updated → Team notified</li>
              <li>✅ Invoice marked as sent → Team notified</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Team Members Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members
              </CardTitle>
              <p className="text-sm text-muted-foreground">Manage team and calendar access</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsShareCalendarOpen(true)} className="gap-2">
                <Share2 className="h-4 w-4" />
                Share Calendar
              </Button>
              <Button size="sm" onClick={() => setIsAddAdminOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Member
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {allAdmins.map((admin) => (
              <div key={admin.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted rounded-lg gap-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: admin.color ? `${admin.color}30` : 'rgba(93, 124, 63, 0.2)' }}
                  >
                    <User className="h-5 w-5" style={{ color: admin.color || '#5d7c3f' }} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{admin.name || admin.email.split('@')[0]}</p>
                      {admin.google_email && (
                        <Badge variant="outline" className="text-xs text-green-700 border-green-200 bg-green-50">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Calendar Connected
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {adminProfile?.id !== admin.id && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteAdmin(admin.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Team Member Dialog */}
      <Dialog open={isAddAdminOpen} onOpenChange={setIsAddAdminOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input 
                value={newAdmin.name}
                onChange={(e) => setNewAdmin(p => ({ ...p, name: e.target.value }))}
                placeholder="Team member's name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin(p => ({ ...p, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input 
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin(p => ({ ...p, password: e.target.value }))}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddAdminOpen(false)}>Cancel</Button>
            <Button onClick={handleAddAdmin}>Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Calendar Dialog */}
      <Dialog open={isShareCalendarOpen} onOpenChange={setIsShareCalendarOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Calendar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share your calendar link with team members or clients to allow them to view your availability.
            </p>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-mono break-all">
                {window.location.origin}/calendar
              </p>
            </div>
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/calendar`);
                toast.success("Calendar link copied!");
              }}
              className="w-full"
            >
              Copy Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsTab;
